import { ItemListType } from '../../types/common';
import { DictionaryWithMappingType } from '../../types/dictionary';
import { MasterWithMappingType } from '../../types/master';
import {
  buildItemLabelMapFromList,
  collectItemIds,
  findItemReferences,
  formatItemReferencesWithItemName,
  getBlockingReferences,
  getNonBlockingReferences,
} from './findItemReferences';

/**
 * JSON取り込み時の項目ID検証（#3039）。
 *
 * 環境間で設定をコピーする際に項目IDを維持できるようにしたことで、
 * JSONに書かれたIDがそのまま確定IDになる。IDは読み取り結果・マスタ照合・出力設定など
 * 多数の箇所から参照されるため、不正なIDが紛れ込むと既存データとの紐付けが壊れる。
 * 反映前にここで検証する。
 */

/** 新規項目を表すIDマーカー。保存時の normalizeItemListIds が採番する */
const NEW_ITEM_ID = '0';

/**
 * 既存の項目リストから、親IDと「親ID-子ID」の全IDを集めた集合を作る。
 */
const collectExistingIds = (itemList: ItemListType[]): Set<string> => {
  const ids = new Set<string>();
  itemList.forEach(item => {
    if (item.id && item.id !== NEW_ITEM_ID) {
      ids.add(item.id);
    }
    (item.children ?? []).forEach(child => {
      if (child.id && child.id !== NEW_ITEM_ID) {
        ids.add(child.id);
      }
    });
  });
  return ids;
};

/**
 * 取り込むJSONから変換した項目リストのIDを検証する。
 *
 * @param newList  JSONから変換した項目リスト
 * @param currentList 現在の項目リスト（編集モードでの照合用）
 * @param isEditMode 編集モードか。新規作成時は照合対象が無いため未知IDの検証をしない
 * @returns エラーメッセージ。問題なければ null
 */
export const validateImportedItemIds = (
  newList: ItemListType[],
  currentList: ItemListType[],
  isEditMode: boolean,
): string | null => {
  const seenIds = new Set<string>();

  for (const item of newList) {
    // 親項目のID重複チェック（'0' は新規項目マーカーなので重複を許容する）
    if (item.id !== NEW_ITEM_ID) {
      if (seenIds.has(item.id)) {
        return `項目ID "${item.id}" が重複しています。`;
      }
      seenIds.add(item.id);
    }

    for (const child of item.children ?? []) {
      if (child.id === NEW_ITEM_ID) continue;

      if (seenIds.has(child.id)) {
        return `項目ID "${child.id}" が重複しています。`;
      }
      seenIds.add(child.id);

      // 子項目IDは「親ID-連番」形式のため、親IDと対応していなければ不整合。
      // 親が新規（'0'）の場合は子も新規になるべきなので、ここでは検証しない
      if (item.id !== NEW_ITEM_ID && !child.id.startsWith(`${item.id}-`)) {
        return `子項目のID "${child.id}" が親項目のID "${item.id}" と一致しません。`;
      }
    }
  }

  // 新規作成時は照合対象の既存パターンが無いため、未知IDの検証は行わない
  if (!isEditMode) {
    return null;
  }

  // 編集モードでは、既存パターンに存在しないIDを弾く。
  // 参照先の無い項目IDが本番データに紛れ込むのを防ぐため
  const existingIds = collectExistingIds(currentList);

  for (const item of newList) {
    if (item.id !== NEW_ITEM_ID && !existingIds.has(item.id)) {
      return `項目ID "${item.id}" はこのパターンに存在しません。IDを削除すると新規項目として追加されます。`;
    }
    for (const child of item.children ?? []) {
      if (child.id !== NEW_ITEM_ID && !existingIds.has(child.id)) {
        return `項目ID "${child.id}" はこのパターンに存在しません。IDを削除すると新規項目として追加されます。`;
      }
    }
  }

  return null;
};

/**
 * 取り込むJSONで、マスタ照合・辞書変換の照合キーに使われている項目が
 * 消えていないか検証する（#3344）。
 *
 * 照合キーが欠けたマッピングは照合が成立しない。項目削除ボタン経由なら
 * ItemDeleteBlockedModal でブロックされるが、JSON取り込みは項目リストを
 * 丸ごと差し替えるためこの経路でも弾く必要がある。
 *
 * 絞り込みキー・反映先は欠けても照合が成立するため、ここでは弾かない
 * （保存時に cleanupItemReferences が該当参照のみ解除する）。
 *
 * @param newList      JSONから変換した項目リスト
 * @param currentList  現在の項目リスト
 * @param masters      このパターンに紐づくマスタ照合設定
 * @param dictionaries このパターンに紐づく辞書変換設定
 * @returns エラーメッセージ。問題なければ null
 */
export const validateImportedSearchKeyItems = (
  newList: ItemListType[],
  currentList: ItemListType[],
  masters: MasterWithMappingType[],
  dictionaries: DictionaryWithMappingType[],
): string | null => {
  if (masters.length === 0 && dictionaries.length === 0) return null;

  const nextIds = collectItemIds(newList);
  // 現在あって取り込み後に無くなるIDが、照合キーに使われていないか見る
  const removedIds = new Set<string>();
  collectItemIds(currentList).forEach(id => {
    if (!nextIds.has(id)) removedIds.add(id);
  });
  if (removedIds.size === 0) return null;

  const blocking = getBlockingReferences(findItemReferences(removedIds, masters, dictionaries));
  if (blocking.length === 0) return null;

  // 項目名は現在の item_list から引く（マッピング側のラベルは旧データで空のことがある）
  const targets = formatItemReferencesWithItemName(
    blocking,
    buildItemLabelMapFromList(currentList),
  ).join('、');
  return `${targets}は照合キーに使われているため削除できません。先にマスタ照合管理画面から該当の設定を削除してください。`;
};

/**
 * 取り込みで外れるマスタ照合・辞書変換の設定を、差分プレビュー用の文字列で返す（#3344）。
 *
 * 照合キーは validateImportedSearchKeyItems で取り込み前に弾かれるため、
 * ここに残るのは絞り込みキー・反映先・履歴分離キー（＝保存時に自動解除される参照）だけ。
 * 自動で外れる変更なので、反映前に何が外れるかを示して判断材料にする。
 *
 * @returns 「項目名（ファイル名の参照箇所）」形式の文字列。該当なしは空配列
 */
export const collectImportMappingNotices = (
  newList: ItemListType[],
  currentList: ItemListType[],
  masters: MasterWithMappingType[],
  dictionaries: DictionaryWithMappingType[],
): string[] => {
  if (masters.length === 0 && dictionaries.length === 0) return [];

  const nextIds = collectItemIds(newList);
  const removedIds = new Set<string>();
  collectItemIds(currentList).forEach(id => {
    if (!nextIds.has(id)) removedIds.add(id);
  });
  if (removedIds.size === 0) return [];

  // ブロック対象（照合キー）は弾かれているため、ここでは非ブロックの参照のみ出す
  const nonBlocking = getNonBlockingReferences(
    findItemReferences(removedIds, masters, dictionaries),
  );
  if (nonBlocking.length === 0) return [];

  return formatItemReferencesWithItemName(nonBlocking, buildItemLabelMapFromList(currentList));
};
